/**
 * useTutorial Hook
 * Provides access to tutorial state and methods with step management logic
 */

import { useApp } from "../helpers/app-context";
import { tutorialSteps } from "../components/Tutorial/tutorialSteps";
import { getBriefTourSteps, getDetailedTourSteps } from "../components/Tutorial/stepFilters";
import { featureFlags } from "../config/featureFlags";
import { getUser } from "../helpers/utils";
import type { TutorialStep, TutorialArea, TutorialRole } from "../components/Tutorial/types";

export function useTutorial() {
  const {
    tutorialActive,
    currentStepIndex,
    selectedAreas,
    showAreaSelection,
    hasCompletedBefore,
    tourMode,
    startTutorial,
    startBriefTour,
    nextStep: contextNextStep,
    previousStep: contextPreviousStep,
    skipStep: contextSkipStep,
    exitTutorial,
    openAreaSelection,
    closeAreaSelection,
  } = useApp() as any;

  /**
   * Get all available tutorial steps (unfiltered)
   */
  const getAllSteps = (): TutorialStep[] => {
    return tutorialSteps;
  };

  /**
   * Get filtered steps based on tour mode (brief vs. detailed), role, and
   * feature flags. Brief tour steps ignore selected areas; detailed tour
   * steps require at least one selected area.
   */
  const getFilteredSteps = (): TutorialStep[] => {
    const role = (getUser()?.type as TutorialRole) || "U";
    if (tourMode === "brief") {
      return getBriefTourSteps(role, featureFlags);
    }
    if (!selectedAreas || selectedAreas.length === 0) return [];
    return getDetailedTourSteps(selectedAreas as TutorialArea[], role, featureFlags);
  };

  /**
   * Get the current step object
   */
  const getCurrentStep = (): TutorialStep | null => {
    const filteredSteps = getFilteredSteps();
    if (!filteredSteps || filteredSteps.length === 0) {
      return null;
    }

    if (currentStepIndex < 0 || currentStepIndex >= filteredSteps.length) {
      return null;
    }

    return filteredSteps[currentStepIndex];
  };

  /**
   * Get total number of steps based on selected areas
   */
  const getTotalSteps = (): number => {
    return getFilteredSteps().length;
  };

  /**
   * Check if there's a next step
   */
  const hasNextStep = (): boolean => {
    return currentStepIndex < getTotalSteps() - 1;
  };

  /**
   * Check if there's a previous step
   */
  const hasPreviousStep = (): boolean => {
    return currentStepIndex > 0;
  };

  /**
   * Check if current step is the first step
   */
  const isFirstStep = (): boolean => {
    return currentStepIndex === 0;
  };

  /**
   * Check if current step is the last step
   */
  const isLastStep = (): boolean => {
    return currentStepIndex === getTotalSteps() - 1;
  };

  /**
   * Move to next step (pass total steps from context)
   */
  const nextStep = () => {
    const totalSteps = getTotalSteps();
    contextNextStep(totalSteps);
  };

  /**
   * Move to previous step
   */
  const previousStep = () => {
    contextPreviousStep();
  };

  /**
   * Skip current step
   */
  const skipStep = () => {
    const totalSteps = getTotalSteps();
    contextSkipStep(totalSteps);
  };

  /**
   * Get progress percentage (0-100)
   */
  const getProgressPercentage = (): number => {
    const total = getTotalSteps();
    if (total === 0) return 0;
    return Math.round(((currentStepIndex + 1) / total) * 100);
  };

  return {
    // State
    isActive: tutorialActive,
    currentStepIndex,
    selectedAreas,
    showAreaSelection,
    hasCompletedBefore,
    tourMode,

    // Methods
    startTutorial,
    startBriefTour,
    nextStep,
    previousStep,
    skipStep,
    exitTutorial,
    openAreaSelection,
    closeAreaSelection,

    // Step helpers
    getAllSteps,
    getFilteredSteps,
    getCurrentStep,
    getTotalSteps,
    hasNextStep,
    hasPreviousStep,
    isFirstStep,
    isLastStep,
    getProgressPercentage,
  };
}
